import { Component, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';

type PortalRole = 'OPS' | 'ADMIN' | 'TINYPROMO_OPS';

@Component({
  selector: 'app-portal-operators',
  templateUrl: './portal-operators.component.html',
  styleUrls: ['./portal-operators.component.scss'],
})
export class PortalOperatorsComponent implements OnInit {
  private readonly base = `${environment.apiUrl}/admin-center/ops-royal/v1`;
  data: any[] = [];
  isLoading = false;
  isSaving = false;
  email = '';
  role: PortalRole = 'OPS';
  filterText = '';
  status: { severity: 'success' | 'error' | 'warn'; text: string } | null = null;
  lastAddedEmail: string | null = null;

  readonly roleOptions: { label: string; value: PortalRole }[] = [
    { label: 'Ops', value: 'OPS' },
    { label: 'Admin', value: 'ADMIN' },
    { label: 'TinyPromo Ops', value: 'TINYPROMO_OPS' },
  ];

  constructor(
    private http: HttpService,
    private messages: MessageService,
    private confirmation: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  get filteredData(): any[] {
    const q = (this.filterText || '').trim().toLowerCase();
    if (!q) {
      return this.data;
    }
    return this.data.filter((row) => {
      const email = (row?.email || '').toLowerCase();
      const role = (row?.role || '').toLowerCase();
      return email.includes(q) || role.includes(q);
    });
  }

  reload(): void {
    this.isLoading = true;
    // Load all roles so seeded WhiteList ADMIN users are visible (not OPS-only).
    this.http.doGet(`${this.base}/portal-operators`).subscribe({
      next: (rows) => {
        this.data = this.sortOperators(rows || []);
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        const detail = this.errorDetail(err, 'Failed to load portal users');
        this.status = { severity: 'error', text: detail };
        this.messages.add({ severity: 'error', summary: 'Error', detail, life: 6000 });
      },
    });
  }

  create(): void {
    const email = (this.email || '').trim().toLowerCase();
    if (!email) {
      this.status = { severity: 'warn', text: 'Enter an email address before clicking Add.' };
      this.messages.add({
        severity: 'warn',
        summary: 'Email required',
        detail: 'Enter an email address before clicking Add.',
        life: 5000,
      });
      return;
    }

    this.isSaving = true;
    this.status = null;
    this.http.doPost(`${this.base}/portal-operators`, { email, role: this.role }).subscribe({
      next: (row: any) => {
        this.isSaving = false;
        this.email = '';
        this.lastAddedEmail = email;
        const roleLabel = row?.role || this.role;
        const wasExisting = this.data.some((r) => (r.email || '').toLowerCase() === email);
        const action = wasExisting ? 'Updated' : 'Added';
        this.status = {
          severity: 'success',
          text: `${action} ${email} as ${roleLabel} (active).`,
        };
        this.messages.add({
          severity: 'success',
          summary: action,
          detail: `${email} is active as ${roleLabel}`,
          life: 6000,
        });
        this.reload();
      },
      error: (err) => {
        this.isSaving = false;
        const detail = this.errorDetail(err, 'Create failed');
        this.status = { severity: 'error', text: detail };
        this.messages.add({ severity: 'error', summary: 'Error', detail, life: 8000 });
      },
    });
  }

  setActive(row: any, active: boolean): void {
    this.http.doPatch(`${this.base}/portal-operators/${row.id}`, { active }).subscribe({
      next: () => {
        this.status = {
          severity: 'success',
          text: `${row.email} ${active ? 'activated' : 'deactivated'}.`,
        };
        this.reload();
      },
      error: (err) => {
        const detail = this.errorDetail(err, 'Update failed');
        this.status = { severity: 'error', text: detail };
        this.messages.add({ severity: 'error', summary: 'Error', detail, life: 8000 });
      },
    });
  }

  setRole(row: any, role: PortalRole): void {
    this.http.doPatch(`${this.base}/portal-operators/${row.id}`, { role, active: true }).subscribe({
      next: () => {
        this.lastAddedEmail = row.email;
        this.status = {
          severity: 'success',
          text: `${row.email} set to ${role} and activated.`,
        };
        this.messages.add({
          severity: 'success',
          summary: 'Updated',
          detail: `${row.email} is now ${role}`,
          life: 6000,
        });
        this.reload();
      },
      error: (err) => {
        const detail = this.errorDetail(err, 'Role update failed');
        this.status = { severity: 'error', text: detail };
        this.messages.add({ severity: 'error', summary: 'Error', detail, life: 8000 });
      },
    });
  }

  confirmDelete(row: any): void {
    if (row?.role === 'ADMIN') {
      this.messages.add({
        severity: 'warn',
        summary: 'Not allowed',
        detail: 'Admin portal operators cannot be deleted here. Deactivate instead if needed.',
        life: 6000,
      });
      return;
    }
    this.confirmation.confirm({
      message: `Delete portal user ${row.email}? This removes portal access and clears hub assignments (${row.assignedHubCount || 0} hubs).`,
      header: 'Delete portal user',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.delete(row),
    });
  }

  private delete(row: any): void {
    this.http.doDelete(`${this.base}/portal-operators/${row.id}`).subscribe({
      next: () => {
        this.status = { severity: 'success', text: `Deleted ${row.email}.` };
        this.messages.add({ severity: 'success', summary: 'Deleted', detail: row.email, life: 5000 });
        this.reload();
      },
      error: (err) => {
        const detail = this.errorDetail(err, 'Delete failed');
        this.status = { severity: 'error', text: detail };
        this.messages.add({ severity: 'error', summary: 'Error', detail, life: 8000 });
      },
    });
  }

  private sortOperators(rows: any[]): any[] {
    return [...rows].sort((a, b) => {
      const roleRank = (r: any) => (r?.role === 'ADMIN' ? 0 : r?.role === 'TINYPROMO_OPS' ? 1 : 2);
      const byRole = roleRank(a) - roleRank(b);
      if (byRole !== 0) {
        return byRole;
      }
      return (a?.email || '').localeCompare(b?.email || '', undefined, { sensitivity: 'base' });
    });
  }

  private errorDetail(err: any, fallback: string): string {
    const body = err?.error;
    return body?.details || body?.detail || body?.message || err?.message || fallback;
  }
}
